import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from './client';

export type TimeEntry = Schemas['TimeEntryView'];
export type TimeEntryRequest = Schemas['TimeEntryRequest'];

export const entryKeys = {
  all: (ws: string) => ['workspace', ws, 'time-entries'] as const,
  list: (ws: string, userId: string | undefined, from: string, to: string) =>
    ['workspace', ws, 'time-entries', 'list', userId ?? 'me', from, to] as const,
  running: (ws: string, userId?: string) => ['workspace', ws, 'time-entries', 'running', userId ?? 'me'] as const,
};

export function useTimeEntries(ws: string, from: string, to: string, userId?: string) {
  return useQuery({
    queryKey: entryKeys.list(ws, userId, from, to),
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/workspaces/{workspaceId}/time-entries', {
          params: { path: { workspaceId: ws }, query: { from, to, userId, size: 500 } },
        }),
      ),
  });
}

export function useRunningTimer(ws: string, userId?: string) {
  return useQuery({
    queryKey: entryKeys.running(ws, userId),
    queryFn: async () => {
      const result = await api.GET('/api/v1/workspaces/{workspaceId}/time-entries/running', {
        params: { path: { workspaceId: ws }, query: { userId } },
      });
      if (result.response.status === 204) return null;
      return unwrap(Promise.resolve(result));
    },
    refetchOnWindowFocus: true,
  });
}

function useInvalidateEntries(ws: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: entryKeys.all(ws) });
    void qc.invalidateQueries({ queryKey: ['workspace', ws, 'reports'] });
    void qc.invalidateQueries({ queryKey: ['workspace', ws, 'dashboard'] });
  };
}

export function useCreateEntry(ws: string) {
  const invalidate = useInvalidateEntries(ws);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TimeEntryRequest) =>
      unwrap(api.POST('/api/v1/workspaces/{workspaceId}/time-entries', { params: { path: { workspaceId: ws } }, body })),
    onSuccess: (entry, body) => {
      // Laufenden Timer sofort zeigen (auch offline, wenn kein Nachladen möglich ist)
      if (!entry.end) qc.setQueryData(entryKeys.running(ws, body.userId ?? undefined), entry);
      invalidate();
    },
  });
}

export function useStopTimer(ws: string) {
  const invalidate = useInvalidateEntries(ws);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas['StopRequest'] = {}) =>
      unwrap(api.POST('/api/v1/workspaces/{workspaceId}/time-entries/stop', { params: { path: { workspaceId: ws } }, body })),
    onSuccess: (_entry, body) => {
      qc.setQueryData(entryKeys.running(ws, body?.userId ?? undefined), null);
      invalidate();
    },
  });
}

export function useUpdateEntry(ws: string) {
  const invalidate = useInvalidateEntries(ws);
  return useMutation({
    mutationFn: ({ id, ...body }: TimeEntryRequest & { id: string }) =>
      unwrap(
        api.PATCH('/api/v1/workspaces/{workspaceId}/time-entries/{id}', { params: { path: { workspaceId: ws, id } }, body }),
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteEntry(ws: string) {
  const invalidate = useInvalidateEntries(ws);
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/api/v1/workspaces/{workspaceId}/time-entries/{id}', { params: { path: { workspaceId: ws, id } } })),
    onSuccess: invalidate,
  });
}

export function useEntryAction(ws: string, action: 'duplicate' | 'continue') {
  const invalidate = useInvalidateEntries(ws);
  return useMutation({
    mutationFn: (id: string) =>
      action === 'duplicate'
        ? unwrap(
            api.POST('/api/v1/workspaces/{workspaceId}/time-entries/{id}/duplicate', {
              params: { path: { workspaceId: ws, id } },
            }),
          )
        : unwrap(
            api.POST('/api/v1/workspaces/{workspaceId}/time-entries/{id}/continue', {
              params: { path: { workspaceId: ws, id } },
            }),
          ),
    onSuccess: invalidate,
  });
}

export function useSplitEntry(ws: string) {
  const invalidate = useInvalidateEntries(ws);
  return useMutation({
    mutationFn: ({ id, at }: { id: string; at: string }) =>
      unwrap(
        api.POST('/api/v1/workspaces/{workspaceId}/time-entries/{id}/split', {
          params: { path: { workspaceId: ws, id } },
          body: { at },
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useBulkUpdate(ws: string) {
  const invalidate = useInvalidateEntries(ws);
  return useMutation({
    mutationFn: (body: Schemas['BulkUpdateRequest']) =>
      unwrap(
        api.POST('/api/v1/workspaces/{workspaceId}/time-entries/bulk-update', { params: { path: { workspaceId: ws } }, body }),
      ),
    onSuccess: invalidate,
  });
}

export function useBulkDelete(ws: string) {
  const invalidate = useInvalidateEntries(ws);
  return useMutation({
    mutationFn: (ids: string[]) =>
      unwrap(
        api.POST('/api/v1/workspaces/{workspaceId}/time-entries/bulk-delete', {
          params: { path: { workspaceId: ws } },
          body: { ids },
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useSuggestions(ws: string, q: string, enabled: boolean) {
  return useQuery({
    queryKey: ['workspace', ws, 'time-entries', 'suggestions', q],
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/workspaces/{workspaceId}/time-entries/suggestions', {
          params: { path: { workspaceId: ws }, query: { q, limit: 8 } },
        }),
      ),
    enabled,
    staleTime: 30_000,
  });
}

/** Wandelt einen gelesenen Eintrag in einen Update-Request (Vollaktualisierung). */
export function toRequest(entry: TimeEntry, changes: Partial<TimeEntryRequest> = {}): TimeEntryRequest {
  return {
    userId: entry.userId,
    description: entry.description,
    projectId: entry.projectId,
    taskId: entry.taskId,
    tagIds: entry.tagIds,
    billable: entry.billable,
    start: entry.start,
    end: entry.end,
    type: entry.type,
    ...changes,
  };
}
